import { useMemo } from 'react';
import { Select, SelectItem } from '@scholastic/mockingjay';
import { type MultiSelectOption } from 'eds-common';
import { LoadingSelect } from '@data/components/basic/LoadingSelect/LoadingSelect';
import { type ProductCode } from '@data/main';
import { useGetClassesByEdAppApi } from '@data/hooks/api/use-get-classes-by-ed-app-api/use-get-classes-by-ed-app-api';
import { LabeledMultiSelect } from '../../LabeledMultiSelect/LabeledMultiSelect';

interface ClassDropdownBaseProps {
  orgId: string;
  userId: string;
  edApp: ProductCode;
}

type ClassDropdownProps =
  | (ClassDropdownBaseProps & {
      mode: 'multiselect';
      selectedClassIds: string[];
      onChange: (classIds: string[]) => void;
    })
  | (ClassDropdownBaseProps & {
      mode?: 'single';
      selectedClassId: string;
      onChange: (classId: string) => void;
    })
  | (ClassDropdownBaseProps & {
      mode: 'locked';
      lockedClassId: string;
    });

export function ClassDropdown({
  orgId,
  userId,
  edApp,
  mode,
  ...rest
}: ClassDropdownProps) {
  const {
    data: classes,
    isLoading,
    isError,
  } = useGetClassesByEdAppApi({ orgId, userId, edApp }, { retry: 0 });

  const sortedClasses = useMemo(
    () =>
      [...(classes ?? [])]
        .sort((a, b) => a.name.localeCompare(b.name))
        .filter((cls) => cls.hasStudentsEnrolled),
    [classes]
  );

  const classOptions: MultiSelectOption[] = useMemo(
    () =>
      sortedClasses.map((cls) => ({
        value: cls.classId,
        label: cls.name,
      })),
    [sortedClasses]
  );

  if (isLoading) return <LoadingSelect />;
  if (isError) throw new Error('Unable to fetch classes');

  // ── Locked mode ─────────────────────────────────────────────────────────────
  if (mode === 'locked') {
    const { lockedClassId } = rest as { lockedClassId: string };
    const lockedClass = sortedClasses.find(
      (cls) => cls.classId === lockedClassId
    );
    return (
      <div className="flex w-full [&>div]:w-full [&_button]:min-w-0">
        <Select label="Classes" value={lockedClassId} disabled>
          <SelectItem value={lockedClassId}>
            {lockedClass?.name ?? lockedClassId}
          </SelectItem>
        </Select>
      </div>
    );
  }

  // ── Multiselect mode ─────────────────────────────────────────────────────────
  if (mode === 'multiselect') {
    const { selectedClassIds, onChange: onMultiChange } = rest as {
      selectedClassIds: string[];
      onChange: (classIds: string[]) => void;
    };
    return (
      <LabeledMultiSelect
        label="Classes"
        inputId="class-multiselect"
        selectAllLabel="All classes"
        options={classOptions}
        selectedIds={selectedClassIds}
        onChange={onMultiChange}
      />
    );
  }

  // ── Single select mode ───────────────────────────────────────────────────────
  const { selectedClassId, onChange: onSingleChange } = rest as {
    selectedClassId: string;
    onChange: (classId: string) => void;
  };

  return (
    <div className="flex w-full [&>div]:w-full [&_button]:min-w-0">
      <Select
        label="Classes"
        value={selectedClassId}
        onValueChange={onSingleChange}
      >
        {sortedClasses.map((cls) => (
          <SelectItem key={cls.classId} value={cls.classId}>
            {cls.name}
          </SelectItem>
        ))}
      </Select>
    </div>
  );
}
